// Restrict-customer state for the rail's Customer tab "..." menu (AI-1359
// part B, FR-67). Moved out of `useInboxActions`: the trigger lives in the
// rail, a sibling of ThreadPane, so the rail owns the mutation and modal.
// Restricting resolves THIS case; the customer's other open escalations
// resolve too only when the modal's close-others box stays checked (FR-67).
import type { SerializedError } from '@reduxjs/toolkit'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { alert } from 'actions/flash'
import type { FeedbackInboxDetail } from 'api/feedbackInbox.types'
import { useRestrictCustomerMutation } from 'api/recoveryAi'
import type { RestrictCustomerParams } from 'api/recoveryAi.types'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import { useCallback, useEffect, useState } from 'react'

const t = buildTranslate('feedback_inbox.rail.customer')

// Seconds the success flash stays up (matches CaseDetail's send flash).
const SUCCESS_FLASH_TIMEOUT = 5

export interface UseCustomerRestrictionOptions {
  detail: FeedbackInboxDetail | undefined
  // The server's restrict gate (`manage MerchantSettings`) — passed in so the
  // hook's own tests stay AbilityContext-free.
  canManageMerchantSettings: boolean
  announce?: (text: string) => void
}

export interface CustomerRestriction {
  // RecoveryAI case with a conversation — the POST is keyed by its id, so a
  // plain feedback item has no restrict action at all (prototype v83).
  eligible: boolean
  conversationId: string | null
  // Why the menu item is disabled (no permission / already restricted);
  // undefined when it's actionable.
  disabledReason: string | undefined
  isOpen: boolean
  open: () => void
  close: () => void
  confirm: (params: RestrictCustomerParams) => Promise<void>
  isSubmitting: boolean
  isError: boolean
  error: FetchBaseQueryError | SerializedError | undefined
  resetError: () => void
}

export function useCustomerRestriction({
  detail,
  canManageMerchantSettings,
  announce,
}: UseCustomerRestrictionOptions): CustomerRestriction {
  const dispatch = useDispatch()
  const [trigger, state] = useRestrictCustomerMutation()
  const [isOpen, setIsOpen] = useState(false)

  const conversationId =
    detail?.kind === 'recovery_ai' ? detail.conversationId : null
  // Unlike CaseDetail (which hides restrict once `isCaseClosed`), eligibility
  // here doesn't check `detail.status`: nexus's `RestrictAndResolve` already
  // allows restricting a resolved conversation, so a resolved inbox item is
  // deliberately still restrictable rather than mirroring CaseDetail's rule.
  const eligible = !!conversationId
  const alreadyRestricted = !!detail?.customer.restricted

  let disabledReason: string | undefined
  if (!canManageMerchantSettings) disabledReason = t('restrict_no_permission')
  else if (alreadyRestricted) disabledReason = t('restrict_already_restricted')

  // Mutation state survives navigation — reset it (and close the modal) on
  // an item change so one case's error never shows on the next.
  useEffect(() => {
    state.reset()
    setIsOpen(false)
    // `state` is a fresh object every render; only the item change matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.id])

  const actionable = eligible && disabledReason === undefined
  const open = useCallback(() => {
    if (actionable) setIsOpen(true)
  }, [actionable])
  const close = useCallback(() => setIsOpen(false), [])

  // Rejects on failure so the modal keeps itself open with its banner.
  const confirm = useCallback(
    async (params: RestrictCustomerParams) => {
      await trigger(params).unwrap()
      const othersClosed = params.close_escalation_ids?.length ?? 0
      const message =
        othersClosed > 0
          ? t('restrict_success_with_others', { count: othersClosed })
          : t('restrict_success')
      dispatch(
        alert({ key: 'success', message, timeout: SUCCESS_FLASH_TIMEOUT })
      )
      announce?.(message)
    },
    [trigger, dispatch, announce]
  )

  return {
    eligible,
    conversationId,
    disabledReason,
    isOpen,
    open,
    close,
    confirm,
    isSubmitting: state.isLoading,
    isError: state.isError,
    error: state.error,
    resetError: state.reset,
  }
}
